import React, { useEffect, useRef } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

// Generate hours array (00-23)
const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i.toString().padStart(2, "0"),
  label: i.toString().padStart(2, "0"),
}));

// Generate minutes array based on interval
const getMinuteOptions = (interval: 1 | 15 | 30 | 60) => {
  switch (interval) {
    case 1:
      return Array.from({ length: 60 }, (_, i) => ({
        value: i.toString().padStart(2, "0"),
        label: i.toString().padStart(2, "0"),
      }));
    case 30:
      return ["00", "30"].map((m) => ({ value: m, label: m }));
    case 60:
      return ["00"].map((m) => ({ value: m, label: m }));
    default:
      // Default to showing 0-59 minutes for full granularity
      return Array.from({ length: 60 }, (_, i) => ({
        value: i.toString().padStart(2, "0"),
        label: i.toString().padStart(2, "0"),
      }));
  }
};

interface CompactTimeSelectProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  minuteInterval?: 1 | 15 | 30 | 60;
  placeholder?: string;
  size?: "sm" | "md";
}

// Auto-scroll to selected item in dropdown
const useAutoScroll = (isOpen: boolean, selectedValue: string) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen && selectedValue) {
      // Small delay to ensure DOM is rendered
      timeoutRef.current = setTimeout(() => {
        const selectedItem = document.querySelector(
          `[data-value="${selectedValue}"][data-radix-select-item]`
        );
        if (selectedItem && selectedItem instanceof HTMLElement) {
          // Find the scrollable parent (listbox)
          const scrollableParent = selectedItem.closest('[role="listbox"]');
          if (scrollableParent) {
            const itemOffsetTop = selectedItem.offsetTop;
            const parentHeight = scrollableParent.clientHeight;

            // Calculate if item is visible and center it
            const targetScrollTop =
              itemOffsetTop - parentHeight / 2 + selectedItem.clientHeight / 2;
            scrollableParent.scrollTop = Math.max(0, targetScrollTop);
          } else {
            // Fallback to scrollIntoView
            selectedItem.scrollIntoView({
              behavior: "instant",
              block: "center",
            });
          }
        }
      }, 100);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOpen, selectedValue]);
};

export const CompactTimeSelect: React.FC<CompactTimeSelectProps> = ({
  value,
  onChange,
  disabled = false,
  className = "",
  minuteInterval = 15,
  size = "sm",
}) => {
  const [hour, minute] = value ? value.split(":") : ["", ""];
  const [hourOpen, setHourOpen] = React.useState(false);
  const [minuteOpen, setMinuteOpen] = React.useState(false);

  const handleHourChange = (newHour: string) => {
    onChange(`${newHour}:${minute || "00"}`);
    setHourOpen(false);
  };

  const handleMinuteChange = (newMinute: string) => {
    onChange(`${hour || "00"}:${newMinute}`);
    setMinuteOpen(false);
  };

  const minuteOptions = getMinuteOptions(minuteInterval);

  const baseTriggerClass = size === "sm" ? "h-8 text-sm" : "h-9 text-base";
  const widthClass = size === "sm" ? "w-16" : "w-20";
  const isFlex = className?.includes("flex-1");

  const triggerClass = isFlex
    ? cn(baseTriggerClass, "flex-1 min-w-0")
    : cn(baseTriggerClass, widthClass);

  // Auto-scroll hooks for both hour and minute dropdowns
  useAutoScroll(hourOpen, hour);
  useAutoScroll(minuteOpen, minute);

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <Select
        value={hour}
        onValueChange={handleHourChange}
        disabled={disabled}
        open={hourOpen}
        onOpenChange={setHourOpen}
      >
        <SelectTrigger dataTestId="hour-select" className={cn(triggerClass, "bg-background")}>
          <SelectValue placeholder="--" />
        </SelectTrigger>
        <SelectContent>
          {HOURS.map((h) => (
            <SelectItem key={h.value} value={h.value}>
              {h.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <span
        className={cn(
          "text-muted-foreground flex-shrink-0",
          size === "sm" ? "text-sm" : "text-base"
        )}
      >
        :
      </span>

      <Select
        value={minute}
        onValueChange={handleMinuteChange}
        disabled={disabled}
        open={minuteOpen}
        onOpenChange={setMinuteOpen}
      >
        <SelectTrigger dataTestId="minute-select" className={cn(triggerClass, "bg-background")}>
          <SelectValue placeholder="--" />
        </SelectTrigger>
        <SelectContent>
          {minuteOptions.map((m) => (
            <SelectItem key={m.value} value={m.value}>
              {m.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// Wrapper for appointment time selection (1-minute intervals)
export const AppointmentTimeSelect: React.FC<Omit<CompactTimeSelectProps, "minuteInterval">> = (
  props
) => <CompactTimeSelect {...props} minuteInterval={1} />;

// Wrapper for business hours selection (15-minute intervals)
export const BusinessHoursTimeSelect: React.FC<Omit<CompactTimeSelectProps, "minuteInterval">> = (
  props
) => <CompactTimeSelect {...props} minuteInterval={15} />;
